import {useChatbotContext} from "@components/Chatbot/context";
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {getAction, getParams, getFunctionLabel} from "@components/Chatbot/PlanEditor/utils";
import {PARAM_NAMESPACES, PARAM_TYPES} from "@components/Chatbot/PlanEditor/enums";
import {
    StringInput,
    DateSingleInput,
    DateMultiInput,
    DateRangeInput,
    DateRangeMultiInput,
    BooleanInput,
    StringListInput,
    IntegerInput,
    DictListInput,
    MetricSingleInput,
    MetricMultiInput,
    PlayerSingleInput,
    PlayerMultiInput, PositionsSingleInput, PositionsMultiInput, PlayerGroupsMultiInput, StringSelectionInput
} from "@components/Chatbot/PlanEditor/inputs";
import {TrainingAPI} from "@api/TrainingAPI";
import {MdOutlinePsychology, MdPsychology} from "react-icons/md";
import {BsChatLeftText} from "react-icons/bs";
import {FaChartSimple} from "react-icons/fa6";
import {FaTable} from "react-icons/fa";
import {Button} from "antd";

let trainingAPI = new TrainingAPI();


const testParams = {
    "statuses": [],
    "flags": [],
    "treatments": [],
    "locations": [],
    "application_times": [],
    "match_mode": "any",
    "injury_statuses": [],
    "injury_types": [],

    "opponents": [],
    "start_date": "2023-01-01",
    "end_date": "2023-01-31",
    "games_only": true,
    "n": 3,

    "date": "2023-02-15",
    "date_range": ["2023-05-01", "2023-05-10"],
    "dates": ["2023-03-01", "2023-03-15"],
    "date_ranges": [["2023-04-01", "2023-04-07"], ["2023-04-15", "2023-04-20"]],
    "dates_to_compare": ["2023-06-01", "2023-06-15"],
    "exclude_games": false,
    "trainings_only": true,
    "all_players": false,
    "is_minimum": true,
    "include_acwr": false,
    "weekly": false,
    "all_gps_metrics": false,
    "top_n": 5,
    "top_n_desc": true,
    "top_x": 3,
    "top_x_desc": false,
    "count": 3,
    "metric_names": ["player_load", "distance"],
    // "metrics": ["CMJ - mRSI"],
    "metrics": ["accel"],
    "metric": "accel_decel",
    "top_x_metric": "distance",
    "player_names": ["John Doe"],
    "player_name": "Joel Black",
    "positions": ["2/3"],
    "player_groups": ["Starters"],
    "targets": [],
    "additional_metrics": [
        "accel"
    ],
    "group_by": "position",
    "stat": "avg",
    "top_x_stat": "sum",
}

const PlanEditorPanel = ({ defaultCollapsed }) => {
    const {
        messages,
        actionMetadata,
        conversationId,
        streamingUuid,
        appendNewMessage,
        selectedMessage,
        setIsTyping,
    } = useChatbotContext();
    const [collapsed, setCollapsed] = useState(defaultCollapsed ?? true);
    const [newParams, setNewParams] = useState({});
    const [message, setMessage] = useState(selectedMessage);

    const {
        info: {
            id: teamId,
        }
    } = useSelector(({ team }) => team);

    useEffect(() => {
        setCollapsed(defaultCollapsed ?? true);
        setMessage(selectedMessage);
        setNewParams(getParams(selectedMessage));
    }, [selectedMessage?.id]);

    const canRenderPanel = () => {
        const action = getAction(message);
        if (!action) return false;
        return !(action?.action !== "python_function");
    }

    const updateParamValue = (key, value) => {
        setNewParams(prevParams => {
            return {
                ...prevParams,
                [key]: value
            }
        });
    }

    const getFunctionConfig = (action) => {
        const fn = action?.function_to_call?.name;
        return actionMetadata?.functions[fn];
    }

    const getParameterConfig = (key) => {
        return actionMetadata?.parameters?.[key] || {};
    }

    const renderParamRow = (action, key, value) => {
        const fn = action?.function_to_call?.name;
        const fnConfig = getFunctionConfig(action);
        const parameterConfig = getParameterConfig(key);

        const paramLabel = parameterConfig?.label || key;
        let paramType = parameterConfig?.type || "";
        if (fnConfig?.param_type_override && fnConfig?.param_type_override[key]) {
            paramType = fnConfig.param_type_override[key];
        }
        const paramNamespace = parameterConfig?.namespace || "";

        if (paramNamespace === PARAM_NAMESPACES.METRICS) {
            if (paramType === PARAM_TYPES.STRING) {
                return (
                    <MetricSingleInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        functionConfig={fnConfig}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
            else if (paramType === PARAM_TYPES.STRING_LIST) {
                return (
                    <MetricMultiInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        isMulti={true}
                        functionConfig={fnConfig}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
        }

        if (paramNamespace === PARAM_NAMESPACES.PLAYERS) {
            if (paramType === PARAM_TYPES.STRING) {
                return (
                    <PlayerSingleInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        functionConfig={fnConfig}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            } else if (paramType === PARAM_TYPES.STRING_LIST) {
                return (
                    <PlayerMultiInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        functionConfig={fnConfig}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
        }

        if (paramNamespace === PARAM_NAMESPACES.POSITIONS) {
            if (paramType === PARAM_TYPES.STRING) {
                return (
                    <PositionsSingleInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        functionConfig={fnConfig}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            } else if (paramType === PARAM_TYPES.STRING_LIST) {
                return (
                    <PositionsMultiInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        functionConfig={fnConfig}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
        }

        if (paramNamespace === PARAM_NAMESPACES.PLAYER_GROUPS) {
            if (paramType === PARAM_TYPES.STRING_LIST) {
                return (
                    <PlayerGroupsMultiInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        functionConfig={fnConfig}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
        }

        if (paramType === PARAM_TYPES.INTEGER) {
            return (
                <IntegerInput
                    key={key}
                    label={paramLabel}
                    value={value}
                    parameterConfig={parameterConfig}
                    onChange={(value) => {
                        updateParamValue(key, value);
                    }}
                />
            )
        }

        if (key === "start_date:end_date") {
            const parameterConfig = getParameterConfig("start_date");
            return (
                <DateRangeInput
                    key={key}
                    label="Date Range"
                    dateRange={value}
                    parameterConfig={parameterConfig}
                    onChange={(newValue) => {
                        const [startDate, endDate] = newValue;
                        updateParamValue("start_date", startDate);
                        updateParamValue("end_date", endDate);
                    }}
                />
            )
        }

        if (paramNamespace === "dates") {
            if (paramType === PARAM_TYPES.DATE_RANGE) {
                return (
                    <DateRangeInput
                        key={key}
                        label={paramLabel}
                        dateRange={value}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
            if (paramType === PARAM_TYPES.DATE_LIST) {
                return (
                    <DateMultiInput
                        key={key}
                        label={paramLabel}
                        dates={value}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
            if (paramType === PARAM_TYPES.DATE_RANGE_LIST) {
                return (
                    <DateRangeMultiInput
                        key={key}
                        label={paramLabel}
                        dateRanges={value}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
            return (
                <DateSingleInput
                    key={key}
                    label={paramLabel}
                    date={value}
                    parameterConfig={parameterConfig}
                    onChange={(newValue) => {
                        updateParamValue(key, newValue);
                    }}
                />
            )

        }

        if (paramType === PARAM_TYPES.STRING) {
            const functionOptions = parameterConfig?.options_per_function ? parameterConfig?.options_per_function[fn] : null;
            if (functionOptions && functionOptions?.length) {
                return (
                    <StringSelectionInput
                        key={key}
                        label={paramLabel}
                        value={value}
                        options={functionOptions}
                        parameterConfig={parameterConfig}
                        onChange={(newValue) => {
                            updateParamValue(key, newValue);
                        }}
                    />
                )
            }
            return (
                <StringInput
                    key={key}
                    label={paramLabel}
                    value={value}
                    parameterConfig={parameterConfig}
                    onChange={(newValue) => {
                        updateParamValue(key, newValue);
                    }}
                />
            )
        }

        if (paramType === PARAM_TYPES.STRING_LIST) {
            return (
                <StringListInput
                    key={key}
                    label={paramLabel}
                    value={value}
                    parameterConfig={parameterConfig}
                    onChange={(newValue) => {
                        updateParamValue(key, newValue);
                    }}
                />
            )
        }

        if (paramType === PARAM_TYPES.BOOLEAN) {
            return (
                <BooleanInput
                    key={key}
                    label={paramLabel}
                    value={value}
                    parameterConfig={parameterConfig}
                    onChange={(newValue) => {
                        updateParamValue(key, newValue);
                    }}
                />
            )
        }

        if (paramType === PARAM_TYPES.DICT_LIST) {
            return (
                <DictListInput
                    key={key}
                    label={paramLabel}
                    value={value}
                    parameterConfig={parameterConfig}
                />
            )
        }

        return (
            <div key={key} className="chatbot-editable-action-param-row">
                <p className="chatbot-editable-action-param-key">{key}:</p>
                <input
                    type="text"
                    className="chatbot-editable-action-param-value-input"
                    value={value}
                    onChange={(e) => {
                        updateParamValue(key, e.target.value);
                    }}
                />
            </div>
        )
    }

    const resendMessageWithUpdatedParams = async () => {
        const _action = getAction(message);
        const newAction = {
            ..._action,
            function_to_call: {
                ..._action.function_to_call,
                params: {
                    ...newParams,
                    team_id: teamId
                }
            }
        }
        // get user message that triggered this action
        const agentMessageId = message.id;
        const agentMessageIndex = messages.findIndex(m => m.id === agentMessageId);
        const userMessageIndex = agentMessageIndex >= 0 ? agentMessageIndex - 1 : 0;
        const userMessage = messages[userMessageIndex];
        const userMessageText = userMessage?.content?.message || "";

        appendNewMessage({
            id: Date.now(),
            created: Date.now(),
            version: "0.1",
            metadata: {},
            content: {
                message: userMessageText,
                type: "text"
            },
            sender: "user"
        });
        setIsTyping(true);

        const res = await trainingAPI.sendChatbotMessageWithAction({
            inboundMessage: userMessageText,
            action: newAction,
            conversationId,
            streamingUuid
        });

        appendNewMessage({
            created: Date.now(),
            id: Date.now(),
            content: res.outbound_content,
            metadata: res.metadata,
            sender: res.sender,
            version: res.version
        });

        setIsTyping(false);
    }

    const renderMessageTypeIcon = (messageType) => {
        return {
            "text": () => {return <BsChatLeftText size={"12"} />},
            "chart": () => {return <FaChartSimple size={"14"} />},
            "table": () => {return <FaTable size={"14"} />},
            "summary": () => {return <MdPsychology size={"14"} />},
        }[messageType]?.() || <BsChatLeftText />;
    }

    if (!canRenderPanel()) return <></>;
    const action = getAction(selectedMessage);

    return (
        <div className="chatbot-editable-action-wrapper">
            <div
                className="chatbot-editable-action-button"
                onClick={() => setCollapsed(!collapsed)}
            >
                {/* Collapsed Button Icon + Text */}
                <span className="chatbot-editable-action thinking-icon">
                    <MdOutlinePsychology size={20} />
                </span>
                <p className="chatbot-editable-action function-label">{getFunctionLabel(action?.function_to_call?.name, actionMetadata)}</p>
                <div className="chatbot-editable-action response-type-label">
                    {renderMessageTypeIcon(action?.suggested_response_type)}
                </div>

                <span className="chatbot-editable-action collapse-icon">
                    { collapsed ? "▲" : "▼" }
                </span>
            </div>
            {
                !collapsed ? (
                    <div className="chatbot-editable-action-expanded-panel">
                        {
                            Object.entries(newParams).map(([key, value]) => {
                                if (key === "team_id") return null; // skip team_id
                                if (key === "start_date") {
                                    const dateRange = [value, newParams["end_date"]];
                                    return renderParamRow(action, "start_date:end_date", dateRange);
                                }
                                if (key === "end_date") return null; // skip end_date, already handled in start_date
                                return renderParamRow(action, key, value);
                            })
                        }
                        <div className={"chatbot-editable-action-button-area"}>
                            <Button
                                className="chatbot-editable-action-update-params-button"
                                type="default"
                                disabled={newParams === getParams(message)}
                                onClick={() => {
                                    setNewParams(getParams(message));
                                }}
                            >
                                Reset Changes
                            </Button>
                            <Button
                                className="chatbot-editable-action-update-params-button"
                                onClick={() => {
                                    resendMessageWithUpdatedParams().then();
                                }}
                                type="primary"
                            >
                                Resend Message
                            </Button>
                        </div>

                    </div>
                ) : <></>
            }
        </div>
    )
}

export default PlanEditorPanel;